// Set editörü kırılım saf yardımcıları (spec §6.3, §18 DR3/DR15). Satır türü shared `rowKindsOf`
// (grid/portal/rapor kanallarıyla aynı kural), hesap grubu shared `calculationGroupDecision` (resolve
// ile aynı NULL kuralı: ≥1 üyeli ilk tam grup; açık seçim metrik kimliği taşır). SetTreeEditor,
// IndicatorModal "Kırılım" bölümü ve kaydet çubuğu (describeSetChanges) buradan okur.

import {
  calculationGroupDecision,
  parseFormula,
  rowKindsOf,
  selfBreakdownCalls,
  type KpiBreakdown,
  type KpiNodeKind,
  type KpiRowKind,
  type KpiSetIndicator,
} from "@mahir/shared";
import { KPI_BREAKDOWN_COPY } from "./kpi-breakdown-copy";

/** Hesap grubu kararına yeten asgari set satırı (taslak/orijinal satırları ikisi de uyar). */
export interface BreakdownSetNode {
  id: string;
  parentId: string | null;
  metricId: string;
  orderIndex: number;
  label: string;
  nodeKind?: KpiNodeKind | undefined;
  breakdown?: KpiBreakdown | null | undefined;
  dimensionMember?: string | null | undefined;
  calculationGroupMetricId?: string | null | undefined;
}

export interface SetBreakdownView<T extends BreakdownSetNode> {
  kindOf: (id: string) => KpiRowKind;
  childrenOf: (id: string) => readonly T[];
  /** toplamın grup satırları (sette, orderIndex → etiket) */
  groupsOf: (totalId: string) => readonly T[];
  membersOf: (groupId: string) => readonly T[];
  /** grup → toplam; üye → grubunun toplamı; diğer → null */
  totalOf: (id: string) => T | null;
  /** üye → grubu; diğer → null */
  groupOf: (id: string) => T | null;
  /** kırılım üyesi (grup satırının çocuğu) */
  isMember: (id: string) => boolean;
}

function compare(a: BreakdownSetNode, b: BreakdownSetNode): number {
  return a.orderIndex - b.orderIndex || a.label.localeCompare(b.label, "tr");
}

export function setBreakdownView<T extends BreakdownSetNode>(
  rows: readonly T[],
): SetBreakdownView<T> {
  const kinds = rowKindsOf(rows);
  const byId = new Map(rows.map((r) => [r.id, r]));
  const children = new Map<string, T[]>();
  for (const r of rows) {
    if (!r.parentId || !byId.has(r.parentId)) continue;
    const list = children.get(r.parentId) ?? [];
    list.push(r);
    children.set(r.parentId, list);
  }
  for (const list of children.values()) list.sort(compare);
  const kindOf = (id: string): KpiRowKind => kinds.get(id) ?? "metric";
  const childrenOf = (id: string): readonly T[] => children.get(id) ?? [];
  const parentOf = (id: string): T | null => {
    const p = byId.get(id)?.parentId;
    return p ? (byId.get(p) ?? null) : null;
  };
  const groupOf = (id: string): T | null => {
    const p = parentOf(id);
    return p && kindOf(p.id) === "breakdown_group" ? p : null;
  };
  return {
    kindOf,
    childrenOf,
    groupsOf: (totalId) => childrenOf(totalId).filter((c) => kindOf(c.id) === "breakdown_group"),
    membersOf: (groupId) => childrenOf(groupId),
    totalOf: (id) => {
      const own = kindOf(id) === "breakdown_group" ? id : groupOf(id)?.id;
      if (!own) return null;
      const p = parentOf(own);
      return p && kindOf(p.id) === "total" ? p : null;
    },
    groupOf,
    isMember: (id) => groupOf(id) !== null,
  };
}

/** Toplamın hesap grubu kararı (resolve ile aynı); `explicitId` verilirse taslak seçimi yerine. */
export function calcGroupDecisionFor<T extends BreakdownSetNode>(
  total: BreakdownSetNode,
  view: SetBreakdownView<T>,
  explicitId: string | null = total.calculationGroupMetricId ?? null,
): { derived: boolean; group: T | null } {
  const groups = view.groupsOf(total.id);
  const refs = groups.map((g) => ({
    ...g,
    id: g.metricId,
    memberCount: view.membersOf(g.id).length,
  }));
  const d = calculationGroupDecision(refs, explicitId);
  return { derived: d.derived, group: groups.find((g) => g.metricId === d.group?.id) ?? null };
}

export interface FormulaGroupLock {
  /** formüldeki çağrı metni: `TOPLA_ALT("cinsiyet")` */
  call: string;
  dimension: string;
}

/** Etkin formülde argümanlı `TOPLA_ALT("d")` / `ORT_ALT("d")` → hesap grubu formülden (D7). */
export function formulaGroupLock(
  total: Pick<KpiSetIndicator, "formulas" | "activeFormulaId">,
): FormulaGroupLock | null {
  const active =
    total.formulas.find((f) => f.id === total.activeFormulaId) ??
    total.formulas.find((f) => f.isActive);
  if (!active) return null;
  const p = parseFormula(active.expression);
  if (!p.ok) return null;
  const dimension = selfBreakdownCalls(p.ast).groups[0];
  if (dimension === undefined) return null;
  const fn =
    /\bORT_ALT\s*\(/.test(active.expression) && !/\bTOPLA_ALT\s*\(/.test(active.expression);
  return { call: `${fn ? "ORT_ALT" : "TOPLA_ALT"}("${dimension}")`, dimension };
}

export interface CalcGroupOption {
  metricId: string;
  label: string;
  /** pasif radyo gerekçesi; null = seçilebilir */
  disabledReason: string | null;
}

export interface CalcGroupState<T extends BreakdownSetNode> {
  /** set satırındaki açık seçim (null = Otomatik) */
  explicitId: string | null;
  /** Otomatik (NULL kuralı) olsaydı seçilecek grup */
  auto: T | null;
  /** şu anki hesap grubu (açık seçim ya da Otomatik) */
  current: T | null;
  options: CalcGroupOption[];
  /** formül argümanı → radyo kilitli */
  lock: (FormulaGroupLock & { group: T | null }) | null;
}

/** DR3 — "Toplam nereden hesaplanır" radyosunun karar tablosu (spec §3.2 hesap grubu kuralı). */
export function calcGroupState<T extends BreakdownSetNode>(
  total: BreakdownSetNode & Pick<KpiSetIndicator, "formulas" | "activeFormulaId">,
  view: SetBreakdownView<T>,
): CalcGroupState<T> {
  const C = KPI_BREAKDOWN_COPY.calcGroup;
  const explicitId = total.calculationGroupMetricId ?? null;
  const groups = view.groupsOf(total.id);
  const options = groups.map((g) => ({
    metricId: g.metricId,
    label: g.label,
    disabledReason:
      g.breakdown?.complete === false
        ? C.reasonPartial
        : g.breakdown?.rollup === "none"
          ? C.reasonNone
          : view.membersOf(g.id).length === 0
            ? C.reasonEmpty
            : null,
  }));
  const lock = formulaGroupLock(total);
  return {
    explicitId,
    auto: calcGroupDecisionFor(total, view, null).group,
    current: calcGroupDecisionFor(total, view, explicitId).group,
    options,
    lock: lock
      ? { ...lock, group: groups.find((g) => g.breakdown?.dimension === lock.dimension) ?? null }
      : null,
  };
}

/** Grup listesindeki rol eki: hesap grubu / doğrulama (tam) / seçili kırılımlar. */
export function groupRole<T extends BreakdownSetNode>(
  total: BreakdownSetNode,
  group: BreakdownSetNode,
  view: SetBreakdownView<T>,
): "calc" | "check" | "partial" {
  if (calcGroupDecisionFor(total, view).group?.id === group.id) return "calc";
  return group.breakdown?.complete === false ? "partial" : "check";
}

/** Toplam satırı rozeti (set editörü tek biçim): "kırılım: cinsiyet (2) · yaş grubu (3)". */
export function totalPillText<T extends BreakdownSetNode>(
  total: BreakdownSetNode,
  view: SetBreakdownView<T>,
): string | null {
  if (view.kindOf(total.id) !== "total") return null;
  const C = KPI_BREAKDOWN_COPY;
  const groups = view.groupsOf(total.id).map((g) => ({
    label: C.dimensionName(g.breakdown?.dimension, g.label),
    members: view.membersOf(g.id).length,
  }));
  return `${C.tree.pillPrefix} ${C.pill(groups)}`;
}

/** Üyenin birimi üst göstergeden (§15/1); üye değilse null. */
export function memberParentUnit<T extends BreakdownSetNode & { unit: string | null }>(
  member: BreakdownSetNode,
  view: SetBreakdownView<T>,
): string | null {
  if (!view.isMember(member.id)) return null;
  return view.totalOf(member.id)?.unit ?? null;
}

/**
 * Toplamın `{}` / `{_alt}` birim şablonu için hesap grubunun İLK üyesinin birimi (TODO-KPI-126) —
 * backend `resolvedUnitFor` ve grid ile aynı karar (shared `calculationGroupDecision`); hesap grubu
 * ya da üye yoksa null (şablon çözümsüz kalır → ağaçta amber).
 */
export function firstCalcMemberUnit<T extends BreakdownSetNode & { unit: string | null }>(
  total: BreakdownSetNode,
  view: SetBreakdownView<T>,
): string | null {
  const group = calcGroupDecisionFor(total, view).group;
  return group ? (view.membersOf(group.id)[0]?.unit ?? null) : null;
}

/** Bir üye müşteriye kapalıysa toplam portalda gizlenir (K3) — toplam satırı ipucu. */
export function memberHidesTotal<
  T extends BreakdownSetNode & Pick<KpiSetIndicator, "effectiveClientAccess">,
>(total: BreakdownSetNode, view: SetBreakdownView<T>): boolean {
  return view
    .groupsOf(total.id)
    .some((g) => view.membersOf(g.id).some((m) => m.effectiveClientAccess === "closed"));
}

/**
 * Kaydet çubuğu satırları: taslakta hesap grubu değişen toplamlar (açık seçim ya da grup sırası —
 * NULL kuralı orderIndex'e bakar). Toplam orijinalde de toplam olmalı (eklenen satır "eklendi"dir).
 */
export function calcGroupChangeLines<T extends BreakdownSetNode>(
  original: readonly T[],
  draft: readonly T[],
): { id: string; name: string; text: string }[] {
  const C = KPI_BREAKDOWN_COPY.tree;
  return calcGroupChangeEntries(original, draft).map((e) => ({
    id: e.id,
    name: e.name,
    text: C.calcGroupChange(e.from, e.to),
  }));
}

/** Yapısal hâl: toplam + önce/sonra hesap grubu etiketi (grup yoksa "yok"). İki seviyede de çalışır. */
export function calcGroupChangeEntries<T extends BreakdownSetNode>(
  original: readonly T[],
  draft: readonly T[],
): { id: string; name: string; from: string; to: string }[] {
  const C = KPI_BREAKDOWN_COPY.tree;
  const before = setBreakdownView(original);
  const after = setBreakdownView(draft);
  const origById = new Map(original.map((o) => [o.id, o]));
  const out: { id: string; name: string; from: string; to: string }[] = [];
  for (const d of draft) {
    const o = origById.get(d.id);
    if (!o || after.kindOf(d.id) !== "total" || before.kindOf(o.id) !== "total") continue;
    const a = calcGroupDecisionFor(o, before).group;
    const b = calcGroupDecisionFor(d, after).group;
    if ((a?.metricId ?? null) === (b?.metricId ?? null)) continue;
    out.push({
      id: d.id,
      name: o.label,
      from: a?.label ?? C.noCalcGroup,
      to: b?.label ?? C.noCalcGroup,
    });
  }
  return out;
}
